import React, { useState, useEffect } from 'react';
import { Wifi, Activity, Server, Watch, Zap, Cpu, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from 'axios';

const CHART_TOOLTIP = { contentStyle:{ background:'var(--nd-surface)', border:'1px solid var(--nd-w10)', borderRadius:'10px', boxShadow:'0 8px 24px rgba(0,0,0,0.40)', fontSize:'12px', color:'var(--nd-text)' }, cursor:{ fill:'var(--nd-w04)' } };
const TICK = { fill:'var(--nd-tx30)', fontSize:11 };
const GRID = { strokeDasharray:'3 3', vertical:false, stroke:'var(--nd-w05)' };

const sessionData = [
  { time:'13:45', heartbeat:40, off:20, on:60 },
  { time:'13:58', heartbeat:45, off:25, on:55 },
  { time:'14:15', heartbeat:40, off:22, on:65 },
  { time:'14:32', heartbeat:50, off:30, on:70 },
  { time:'14:45', heartbeat:48, off:28, on:68 },
];
const deviceTypeData = [{ name:'Wearable',count:12 },{ name:'Switch',count:8 },{ name:'Plug',count:15 },{ name:'Lighting',count:25 }];
const healthData = [{ name:'Breaker 63A',health:95,usage:78 },{ name:'Switch 10A',health:88,usage:60 },{ name:'Smart Bulb',health:92,usage:45 },{ name:'Hub',health:98,usage:85 }];

const S = {
  page: { padding:'14px 20px', fontFamily:"'DM Sans',sans-serif" },
  card: { background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', overflow:'hidden' },
  cardHead: { padding:'14px 20px', borderBottom:'1px solid var(--nd-w06)', fontSize:'12px', fontWeight:'700', color:'var(--nd-tx55)', letterSpacing:'0.06em', textTransform:'uppercase' },
  cardBody: { padding:'20px' },
  chartDesc: { fontSize:'11px', color:'var(--nd-tx22)', marginTop:'12px', lineHeight:1.6 },
  grid3: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:'14px', marginBottom:'18px' },
  grid3stat: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'14px', marginBottom:'18px' },
  statCard: { background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'12px', padding:'18px 20px', display:'flex', alignItems:'center', gap:'16px' },
  statIcon: { width:'52px', height:'52px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  statLabel: { fontSize:'13px', fontWeight:'700', color:'var(--nd-text)', marginBottom:'3px' },
  statSub: { fontSize:'12px', color:'var(--nd-tx30)' },
  statVal: { fontFamily:"'DM Mono',monospace", fontSize:'22px', fontWeight:'800', color:'var(--nd-text)' },
  deviceGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'14px' },
  deviceCard: { background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', padding:'28px 20px', textAlign:'center' },
  deviceName: { fontSize:'14px', fontWeight:'800', color:'var(--nd-text)', marginTop:'16px', marginBottom:'12px' },
  onlineBadge: { display:'inline-flex', alignItems:'center', gap:'5px', padding:'4px 12px', borderRadius:'20px', fontSize:'11px', fontWeight:'700', background:'rgba(52,211,153,0.10)', border:'1px solid rgba(52,211,153,0.22)', color:'#34D399', letterSpacing:'0.06em' },
  sectionLabel: { fontSize:'13px', fontWeight:'700', color:'var(--nd-tx55)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'12px', display:'flex', alignItems:'center', gap:'8px' },
};

const AdminIoT = () => {
  const [loading, setLoading] = useState(true);
  const [totalDevices, setTotalDevices] = useState('…');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/v1/devices/count`);
        if (res.data?.totalCount !== undefined) setTotalDevices(res.data.totalCount);
      } catch { setTotalDevices('N/A'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return (
    <div style={{ ...S.page, display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'40px', height:'40px', border:'3px solid var(--nd-w08)', borderTop:'3px solid #3B82F6', borderRadius:'50%', animation:'iot-spin 1s linear infinite', margin:'0 auto' }}/>
        <style>{'@keyframes iot-spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes iot-spin{to{transform:rotate(360deg)}} @keyframes iot-pulse{0%,100%{opacity:.55}50%{opacity:1}}`}</style>

      {/* Header */}
      <div style={{ ...S.card, padding:'20px 24px 18px', marginBottom:'18px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', fontWeight:'700', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--nd-tx25)', marginBottom:'10px' }}>
          <span>Admin</span><ChevronRight size={12} color="var(--nd-w12)"/><span style={{ color:'rgba(34,211,165,0.55)' }}>IoT Platform</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'12px', marginBottom:'14px' }}>
          <div>
            <div style={{ fontSize:'26px', fontWeight:'800', color:'var(--nd-tx85)', letterSpacing:'-0.03em', display:'flex', alignItems:'center', gap:'12px' }}>
              <Cpu size={22} color="#FB923C"/>SOWASH IoT Platform
            </div>
            <div style={{ fontSize:'12px', color:'var(--nd-tx30)', marginTop:'5px' }}>Real-time Device Monitoring & Control</div>
          </div>
          <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'7px 14px', borderRadius:'20px', background:'rgba(52,211,153,0.09)', border:'1px solid rgba(52,211,153,0.20)' }}>
            <span style={{ width:'7px', height:'7px', background:'#34D399', borderRadius:'50%', display:'inline-block', animation:'iot-pulse 2s ease-in-out infinite' }}/>
            <span style={{ fontSize:'12px', fontWeight:'700', color:'#34D399', letterSpacing:'0.06em' }}>System Operational</span>
          </div>
        </div>
        <div style={{ height:'2px', borderRadius:'2px', background:'linear-gradient(90deg,#FB923C,#3B82F6,transparent)' }}/>
      </div>

      {/* Charts */}
      <div style={S.grid3}>
        <div style={S.card}>
          <div style={{ ...S.cardHead, color:'#60A5FA' }}>Recent Sessions</div>
          <div style={S.cardBody}>
            <div style={{ height:'220px' }}>
              <ResponsiveContainer>
                <LineChart data={sessionData}>
                  <CartesianGrid {...GRID}/>
                  <XAxis dataKey="time" axisLine={false} tickLine={false} tick={TICK}/>
                  <YAxis axisLine={false} tickLine={false} tick={TICK}/>
                  <Tooltip {...CHART_TOOLTIP}/>
                  <Legend iconType="circle" wrapperStyle={{ fontSize:'11px', color:'var(--nd-tx45)' }}/>
                  <Line type="monotone" dataKey="heartbeat" stroke="#3B82F6" strokeWidth={2} dot={false} name="Heartbeat"/>
                  <Line type="monotone" dataKey="on" stroke="#22D3A5" strokeWidth={2} dot={false} name="ON"/>
                  <Line type="monotone" dataKey="off" stroke="#F87171" strokeWidth={2} dot={false} name="OFF"/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={S.chartDesc}>Live session activity by timestamp and action type.</div>
          </div>
        </div>

        <div style={S.card}>
          <div style={{ ...S.cardHead, color:'#FBBF24' }}>Devices by Type</div>
          <div style={S.cardBody}>
            <div style={{ height:'220px' }}>
              <ResponsiveContainer>
                <BarChart data={deviceTypeData}>
                  <CartesianGrid {...GRID}/>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={TICK}/>
                  <YAxis axisLine={false} tickLine={false} tick={TICK}/>
                  <Tooltip {...CHART_TOOLTIP}/>
                  <Bar dataKey="count" fill="#FBBF24" radius={[5,5,0,0]} barSize={28} name="Devices" opacity={0.85}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={S.chartDesc}>Total devices grouped by type. Visualizes deployment trends.</div>
          </div>
        </div>

        <div style={S.card}>
          <div style={{ ...S.cardHead, color:'#34D399' }}>Device Health & Usage</div>
          <div style={S.cardBody}>
            <div style={{ height:'220px' }}>
              <ResponsiveContainer>
                <LineChart data={healthData}>
                  <CartesianGrid {...GRID}/>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={TICK}/>
                  <YAxis axisLine={false} tickLine={false} tick={TICK}/>
                  <Tooltip {...CHART_TOOLTIP}/>
                  <Legend iconType="plainline" wrapperStyle={{ fontSize:'11px', color:'var(--nd-tx45)' }}/>
                  <Line type="monotone" dataKey="health" stroke="#34D399" strokeWidth={2} name="Health (%)"/>
                  <Line type="monotone" dataKey="usage" stroke="#FBBF24" strokeWidth={2} name="Usage (%)"/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={S.chartDesc}>Performance monitoring and wear patterns.</div>
          </div>
        </div>
      </div>

      {/* Platform Stats */}
      <div style={S.sectionLabel}><Activity size={14} color="var(--nd-tx35)"/>Platform Stats</div>
      <div style={S.grid3stat}>
        {[
          { icon:<span style={{ fontFamily:"'DM Mono',monospace", fontSize:'22px', fontWeight:'800', color:'#FB923C' }}>{totalDevices}</span>, bg:'rgba(251,146,60,0.12)', border:'rgba(251,146,60,0.20)', label:'Total Devices', sub:'Connected to Tuya Cloud' },
          { icon:<span style={{ fontFamily:"'DM Mono',monospace", fontSize:'15px', fontWeight:'800', color:'#34D399' }}>Live</span>, bg:'rgba(52,211,153,0.10)', border:'rgba(52,211,153,0.18)', label:'Cloud Status', sub:'Tuya API Live' },
          { icon:<span style={{ fontFamily:"'DM Mono',monospace", fontSize:'22px', fontWeight:'800', color:'#A78BFA' }}>12</span>, bg:'rgba(167,139,250,0.10)', border:'rgba(167,139,250,0.18)', label:'Active Sessions', sub:'Last 24 hours' },
        ].map(({ icon, bg, border, label, sub }) => (
          <div key={label} style={S.statCard}>
            <div style={{ ...S.statIcon, background:bg, border:`1px solid ${border}` }}>{icon}</div>
            <div><div style={S.statLabel}>{label}</div><div style={S.statSub}>{sub}</div></div>
          </div>
        ))}
      </div>

      {/* Device Overview */}
      <div style={S.sectionLabel}><Cpu size={14} color="var(--nd-tx35)"/>Device Overview</div>
      <div style={S.deviceGrid}>
        {[
          { icon:<Zap size={64} color="#FBBF24" strokeWidth={1.2}/>, name:'Wifi Breaker 63A' },
          { icon:<Server size={64} color="#3B82F6" strokeWidth={1.2}/>, name:'Wifi Switch 10A' },
          { icon:<Watch size={64} color="#22D3A5" strokeWidth={1.5}/>, name:'SoWash Wristband' },
        ].map(({ icon, name }) => (
          <div key={name} style={S.deviceCard}>
            <div style={{ height:'100px', display:'flex', alignItems:'center', justifyContent:'center' }}>{icon}</div>
            <div style={S.deviceName}>{name}</div>
            <div style={S.onlineBadge}><CheckCircle size={11}/>ONLINE</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminIoT;